import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {gsap} from "gsap";
import {
    SimpleReelModelEventData,
    SimpleReelModelState,
    SimpleReelModelSymbolData
} from "../../core/reel/simplereel/SimpleReelModel";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {ArrayUtils} from "../utils/ArrayUtils";


import {MathUtils} from "../utils/MathUtils";

import {ReelUtils} from "../utils/ReelUtils";
import {BS2Statics} from "../config/BS2Statics";
import {BS2} from "../BS2";
import {BS2GameMode} from "../server/BS2GameMode";
import {BS2Reel} from "../reels/BS2Reel";
import {BS2Sound} from "../config/sound/BS2Sound";
import {
    CollectScattersPresentationAvalanche
} from "../runnerpresentations/maingame/win/CollectScattersPresentationAvalanche";
import {WildSymbol} from "../reels/WildSymbol";

/**
 * Created by Jerker Nord on 2021-01-28.
 */

export class BS2NormalResultPresentation implements FlowStatePresentation {
    private _tl: gsap.core.Timeline;
    private _stoppedReels: boolean[];
    private _bouncedReels: boolean[];
    private _nearWins: (gsap.core.Timeline | undefined)[] = [];
    private _reelStopAnimation: gsap.core.Timeline[];
    private _allReelsStopped: boolean;
    private _tlComplete: boolean;
    private _lastReel: BS2Reel;
    private _nextRandomReelSetStartIndex: number;
    private _featureBetCollectSum: number;

    constructor() {

    }

    public start(flowStateController: FlowStateController) {
        this._nextRandomReelSetStartIndex = 0;
        this._featureBetCollectSum = 0;
        this._allReelsStopped = false;
        this._tlComplete = false;
        this._stoppedReels = [false, false, false, false, false];
        this._bouncedReels = [false, false, false, false, false];
        this._nearWins = [];

        this._tl = gsap.timeline({paused: true, onComplete: () => this.presentationComplete(flowStateController)});

        this.createAllAnimations(flowStateController);


        let jurPadding: number = 0
        if (BS2.model.data.mode == BS2GameMode.NORMAL || BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_1 || BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_2) {
            jurPadding = BS2.gameFlowController.calculatePaddingDuration((BS2Statics.reelStopDelay * 4 + 0.225));   //Standard delays + A single stop is 0.225
            this._tl.add(gsap.to(this, {duration: jurPadding}));
            this.assembleAllAnimations(0);

        } else {
            this.assembleAllAnimations(0);
        }

        this._tl.play(0);
        flowStateController.setSkipCallback(() => this.skip());
    }

    private skip(): void {
        const currentTime: number = this._tl.time();
        for (let anim of this._nearWins) {
            if (anim) {
                this._tl.remove(anim);
                if (anim.isActive()) {
                    anim.progress(1);
                }
                anim.kill();
            }
        }
        this._nearWins = [];
        const stopsToStagger = [];
        //Stagger all not yet started,
        for (let i: number = 0; i < this._reelStopAnimation.length; i++) {
            const childStartTime: number = this._reelStopAnimation[i].startTime();
            if (childStartTime >= currentTime) {
                this._tl.remove(this._reelStopAnimation[i]);
                stopsToStagger.push(this._reelStopAnimation[i].pause(0));
            }
        }


        const stagger = BS2Statics.reelStopDelayQuickStop - this._reelStopAnimation[0].duration();
        for (let anim of stopsToStagger) {
            let stopTime: number = this._tl.duration() + stagger;
            if (stopTime < currentTime) {
                stopTime = currentTime;
            }

            this._tl.add(anim.play(0), stopTime);
        }
    }

    private createAllAnimations(flowStateController: FlowStateController): void {
        this._reelStopAnimation = [];
        for (let i: number = 0; i < BS2.model.clientReels.matrix.length; i++) {
            const reelData: string[] = BS2.model.clientReels.matrix[i];
            const stopAnim: gsap.core.Timeline = BS2.reelView.reels[i].model.stop(reelData).pause(0);
            this._lastReel = BS2.reelView.reels[i];
            this._reelStopAnimation.push(stopAnim);
            BS2.reelView.reels[i].model.events?.once(SimpleReelModelState.STOPPED, (data: SimpleReelModelEventData) => {
                this.onReelStop(data, flowStateController)
            });
            BS2.reelView.reels[i].model.events?.once(SimpleReelModelState.BOUNCE, (data: SimpleReelModelEventData) => {
                this.onReelBounce(BS2.reelView.reels[i]);
            });

            const nearWin: gsap.core.Timeline = this.getNearWin(i, 0);
            if (nearWin.totalDuration() > 0) {
                this._nearWins.push(nearWin);
            } else {
                this._nearWins.push(undefined);
            }
        }
    }

    private assembleAllAnimations(jurPadding: number = 0) {

        let jurPaddingPerReel = jurPadding * 0.25; //There are 4 delayes used.

        //Assemble animation
        //let defaultDelay = BS2.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? BS2Statics.reelStopDelayFastSpin : BS2Statics.reelStopDelay;
        let value = BS2.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? BS2Statics.reelStopDelayFastSpin : BS2Statics.reelStopDelay;
        //  let defaultStagger = defaultDelay - this._reelStopAnimation[0].duration() + jurPaddingPerReel;

        let reelDelays = [
            value * 1.3,
            value * 1.1,
            value * 0.9,
            value * 0.7,
            value * 0.4 //Last one is not used. Since there is no reel stopping after.
        ];

        const firstReelDur = this._reelStopAnimation[0].duration();


        let stagger = 0;
        for (let reel: number = 0; reel < this._reelStopAnimation.length; reel++) {
            let defaultDelay = reelDelays[reel];
            let defaultStagger = defaultDelay - firstReelDur + jurPaddingPerReel;

            const nearWin = this._nearWins[reel];

            if (nearWin) {
                this._tl.add(nearWin.play(0));
                stagger = 0;
            } else if (reel > BS2Statics.firstWinHighlightReel && BS2.model.possibleWinData.combinedReels[reel - 1]) {
                //Prolong possible win.
                const delayMultiplier = MathUtils.map(reel, BS2Statics.firstWinHighlightReel, 5, 2, 4);
                stagger += defaultDelay * delayMultiplier;
            }

            //Reel stop
            if (nearWin) {
                this._tl.add(this._reelStopAnimation[reel].play(0), `>${stagger - this._reelStopAnimation[reel].duration()}`);
            } else {
                this._tl.add(this._reelStopAnimation[reel].play(0), `>${stagger}`);
            }
            stagger = defaultStagger;
        }
    }

    private getNearWin(reelId: number, normalStopDelay: number): gsap.core.Timeline {
        let tl: gsap.core.Timeline = gsap.timeline();

        //Normal near win
        if (BS2.model.nearWinReels[reelId] && (BS2.model.featureBetCollectPerReel[reelId - 1] || 0) > 0) {
            tl.add(() => {
                    BS2.soundPlayer.startNearWin();
                }
            );
            tl.add(BS2.reelView.reels[reelId].getShowNearWin(6));
        } else if (BS2.model.nearWinReels[reelId]) {
            tl.add(gsap.to(this, {duration: 0.3}));
        }

        return tl;
    }

    private onReelBounce(reel: BS2Reel) {
        this._bouncedReels[reel.id] = true;
        this.manageNearWinStop(reel.id);
        ReelUtils.reelBounce(reel.id, reel.model.getAllVisibleSymbols());

        if (BS2.model.nearWinReels[reel.id]) {
            BS2.reelView.reels[reel.id].showNoNearWinOverlay();
        }
        if (reel == this._lastReel) {
            BS2.soundPlayer.stopSound(BS2Sound.REEL_SPINNING);
            BS2.soundPlayer.playSound(BS2Sound.REEL_STOP);
        }

        this.manageFeatureBuyUpdates(reel);

    }

    private manageFeatureBuyUpdates(reel: BS2Reel) {
        const featureBetCollect: number | undefined = BS2.model.featureBetCollectPerReel[reel.id];
        const isFeatureBuySfs: boolean = BS2.model.buyFeatureName === "SUPER_FREESPIN" || BS2.model.buyFeatureName === "FREESPIN";

        if (featureBetCollect != undefined) {
            if (featureBetCollect > 0) {
                if (BS2.model.gameData.isRandomLevel) {
                    this._nextRandomReelSetStartIndex++;
                    BS2.reelView.collectBar.addToBar(featureBetCollect, true, false);
                } else {
                    this._featureBetCollectSum += featureBetCollect;
                    BS2.reelView.collectBar.setValue(this._featureBetCollectSum, true, isFeatureBuySfs).progress(1);
                }
            }


            if (BS2.model.gameData.isRandomLevel) {
                if (BS2.model.featureBetCollectPerReel[reel.id] == 0) {
                    const localSets: string[][] = BS2Statics.FEATURE_BET_REEL_SET.concat();
                    localSets.splice(reel.id + 1, 0, BS2Statics.FEATURE_BET_REEL_SET[this._nextRandomReelSetStartIndex]);
                    BS2.reelView.setReeSet(localSets);
                }
            }
        }
    }

    private getScatterSqueeze(reelId: number): gsap.core.Timeline | undefined {
        const symbolDatas: SimpleReelModelSymbolData[] = BS2.reelView.reels[reelId].model.getAllVisibleSymbols();
        let animation: gsap.core.Timeline | undefined;
        for (let data of symbolDatas) {
            if (data.name === BS2Statics.SYMBOLS.S) {
                if (!BS2.model.isSuperFreespin()) {
                    animation = CollectScattersPresentationAvalanche.getSqueezeAnimation({
                        column: data.reelId,
                        row: data.reelPosition
                    });
                    if (BS2.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
                        animation.timeScale(2);
                    }
                }
                break;
            }
        }

        return animation;
    }

    private onReelStop(data: SimpleReelModelEventData, flowStateController: FlowStateController) {
        const scatterSqueeze: gsap.core.Timeline | undefined = this.getScatterSqueeze(data.reel);
        if (scatterSqueeze) {
            scatterSqueeze.add(() => {
                this._stoppedReels[data.reel] = true;
                this.onReelStop(data, flowStateController);
            }, 1.2)
            return;
        }
        this.putBackStickyWilds(data.reel);

        this._stoppedReels[data.reel] = true;
        if (ArrayUtils.isAllTrue(this._stoppedReels)) {
            this._allReelsStopped = true;
            this.completePresentation(flowStateController);
        }
    }

    private putBackStickyWilds(reelId: number) {
        const stickyReel = BS2.reelView.stickySymbols.matrix[reelId];
        for (let i: number = 0; i < stickyReel.length; i++) {
            const stickyData = stickyReel[i];
            if (stickyData !== null) {
                const symbolToRemove = BS2.reelView.reels[reelId].model.getVisibleSymbolAt(i);
                (<WildSymbol>stickyData.positionable).symbolPosition = {
                    reelID: reelId,
                    position: symbolToRemove.reelPosition
                }
                BS2.reelView.reels[reelId].model.swapSymbol(symbolToRemove, stickyData);
                BS2.reelView.stickySymbols.matrix[reelId][i] = null;
                (<WildSymbol>stickyData.positionable).setSticky(false);
            }
        }
    }

    private presentationComplete(flowStateController: FlowStateController) {
        this._tlComplete = true;
        this.completePresentation(flowStateController)
    }

    private manageNearWinStop(reelId: number) {

        if (reelId == 4) {
            BS2.soundPlayer.stopNearWin(BS2.model.data.nextMode);
        } else if (!BS2.model.nearWinReels[reelId + 1]) {
            BS2.soundPlayer.stopNearWin(BS2.model.data.nextMode);
        }
    }

    private completePresentation(flowStateController: FlowStateController) {
        if (this._tlComplete && this._allReelsStopped) {
            flowStateController.finish();
        }
    }

}